import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types';
import { authenticateUser } from '../services/api';

interface AuthState {
  user: User | null;
  impersonatedBy: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  impersonateUser: (user: User) => void;
  returnToAdmin: () => void;
  logout: () => void;
  updateUser: (data: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      impersonatedBy: null,
      token: null,
      isAuthenticated: false,
      isImpersonating: false,
      loading: false,
      
      login: async (email: string, password: string) => {
        set({ loading: true });
        try {
          const response = await authenticateUser(email, password);
          if (response.success && response.user && response.token) {
            set({
              user: response.user,
              impersonatedBy: null,
              token: response.token,
              isAuthenticated: true,
              isImpersonating: false,
              loading: false,
            });
            localStorage.setItem('authToken', response.token);
            return;
          }
          throw new Error(response.message || 'Authentication failed');
        } catch (error) {
          console.error(`Login error: ${error.message || error}`);
          console.error(`Input email: ${email}, Input password: ${password}`);
          set({ user: null, impersonatedBy: null, token: null, isAuthenticated: false, isImpersonating: false, loading: false });
          throw error;
        }
      },
      
      impersonateUser: (targetUser) => {
        const { user, isImpersonating } = get();
        if (!user) return;

        set({
          user: targetUser,
          impersonatedBy: isImpersonating ? get().impersonatedBy : user,
          isAuthenticated: true,
          isImpersonating: true,
        });
      },

      returnToAdmin: () => {
        const { impersonatedBy } = get();
        if (!impersonatedBy) return;

        set({
          user: impersonatedBy,
          impersonatedBy: null,
          isAuthenticated: true,
          isImpersonating: false,
        });
      },

      logout: () => {
        const { impersonatedBy } = get();

        if (impersonatedBy) {
          set({
            user: impersonatedBy,
            impersonatedBy: null,
            isAuthenticated: true,
            isImpersonating: false,
          });
          return;
        }

        localStorage.removeItem('authToken');
        set({ user: null, impersonatedBy: null, token: null, isAuthenticated: false, isImpersonating: false });
      },
      
      updateUser: (updatedUser) => {
        set((state) => {
          // If user is null, return current state
          if (!state.user) return state;
          
          // Create a completely new user object with all fields 
          // to ensure the persist middleware detects the change
          const newUserData = { 
            ...state.user,
            ...updatedUser 
          };
          
          console.log('Updating user in store:', newUserData);
          
          return { 
            user: newUserData 
          };
        });
      }
    }),
    {
      name: 'auth-storage',
      // Update partialize to ensure all fields are properly persisted
      partialize: (state) => ({ 
        user: state.user ? {
          _id: state.user?._id,
          id: state.user?.id,
          name: state.user?.name,
          email: state.user?.email,
          phone: state.user?.phone,
          role: state.user?.role,
          bankName: state.user?.bankName,
          bankId: state.user?.bankId,
          permissions: state.user?.permissions,
          lastLogin: state.user?.lastLogin
        } : null,
        impersonatedBy: state.impersonatedBy ? {
          _id: state.impersonatedBy?._id,
          id: state.impersonatedBy?.id,
          name: state.impersonatedBy?.name,
          email: state.impersonatedBy?.email,
          phone: state.impersonatedBy?.phone,
          role: state.impersonatedBy?.role,
          bankName: state.impersonatedBy?.bankName,
          bankId: state.impersonatedBy?.bankId,
          permissions: state.impersonatedBy?.permissions,
          lastLogin: state.impersonatedBy?.lastLogin
        } : null,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        isImpersonating: state.isImpersonating
      }),
    }
  )
);
